import { JSX, useCallback, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  BeakerIcon,
  BookOpenIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  FolderIcon,
  MoonIcon,
  SunIcon,
  SwatchIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { Boxes, GitBranch, Menu, X } from 'lucide-react';

import { getEnvironment } from '../config';
import { useAuth } from '../hooks/useAuth';
import { useInvitations } from '../hooks/useInvitations';
import { useLogout } from '../hooks/useLogout';
import { useTenant } from '../hooks/useTenant';
import { usePatternsStore } from '../stores/patternsStore';

import { ApiStatus } from './ApiStatus';

interface MobileMenuProps {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

export function MenuMobile({ isDarkMode, toggleDarkMode }: MobileMenuProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { isDevelopment } = getEnvironment();
  const { user } = useAuth();
  const { handleLogout } = useLogout();
  const { currentOrg, loading: tenantLoading } = useTenant();
  const { data: invitations } = useInvitations();
  const navigate = useNavigate();
  const lastPatternsPath = usePatternsStore(s => s.lastPatternsPath);

  const invitationCount = invitations?.length || 0;
  const selectedClass = 'text-foreground bg-primary/10 dark:bg-primary/10';
  const unselectedClass =
    'text-muted hover:text-foreground hover:bg-surface-elevated dark:hover:bg-surface-elevated';

  const onLogout = useCallback((): void => {
    void handleLogout()
      .then(() => {
        setIsOpen(false);
        void navigate('/login');
      })
      .catch(() => {
        setIsOpen(false);
        void navigate('/login');
      });
  }, [handleLogout, navigate]);

  return (
    <div className="lg:hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary/50 rounded-md"
        aria-label="Toggle menu"
      >
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {isOpen && (
        <div className="absolute top-16 right-0 w-64 mt-2 py-2 bg-surface-elevated rounded-lg shadow-xl border border-default z-50 backdrop-blur-sm">
          <div className="px-4 py-2 border-b border-default">
            <ApiStatus />
          </div>

          {/* Primary nav items */}
          <Link
            to="/docs/getting-started"
            onClick={() => setIsOpen(false)}
            className={`block px-4 py-2 text-sm ${
              location.pathname === '/docs/getting-started' ? selectedClass : unselectedClass
            }`}
          >
            <div className="flex items-center">
              <BeakerIcon className="h-5 w-5 mr-2" />
              Beta
            </div>
          </Link>

          <div className="border-t border-default my-1" />

          <Link
            to="/products"
            onClick={() => setIsOpen(false)}
            className={`block px-4 py-2 text-sm ${
              location.pathname === '/products' ? selectedClass : unselectedClass
            }`}
          >
            <div className="flex items-center">
              <Boxes className="h-5 w-5 mr-2" />
              Products
            </div>
          </Link>

          <Link
            to="/repositories"
            onClick={() => setIsOpen(false)}
            className={`block px-4 py-2 text-sm ${
              location.pathname === '/repositories' ? selectedClass : unselectedClass
            }`}
          >
            <div className="flex items-center">
              <GitBranch className="h-5 w-5 mr-2" />
              Repos
            </div>
          </Link>

          <Link
            to="/projects"
            onClick={() => setIsOpen(false)}
            className={`block px-4 py-2 text-sm ${
              location.pathname.startsWith('/projects') ? selectedClass : unselectedClass
            }`}
          >
            <div className="flex items-center">
              <FolderIcon className="h-5 w-5 mr-2" />
              Projects
            </div>
          </Link>

          {/* More section */}
          <div className="border-t border-default my-1" />
          <p className="text-xs font-semibold uppercase text-muted px-4 py-1">More</p>

          <Link
            to="/jira"
            onClick={() => setIsOpen(false)}
            className={`block pl-6 pr-4 py-2 text-sm ${
              location.pathname === '/jira' ? selectedClass : unselectedClass
            }`}
          >
            <div className="flex items-center">
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7c0 2.4 1.94 4.34 4.34 4.34V2.84a.84.84 0 0 0-.84-.84h-9.63zM6.77 6.8a4.36 4.36 0 0 0 4.35 4.35h1.78v1.7a4.35 4.35 0 0 0 4.34 4.34V7.63a.84.84 0 0 0-.84-.84H6.77zM2 11.6c0 2.4 1.95 4.34 4.35 4.34h1.78v1.72c.01 2.39 1.95 4.34 4.35 4.34v-9.56a.84.84 0 0 0-.84-.84H2z" />
              </svg>
              Jira
            </div>
          </Link>

          <Link
            to={lastPatternsPath}
            onClick={() => setIsOpen(false)}
            className={`block pl-6 pr-4 py-2 text-sm ${
              location.pathname.startsWith('/patterns') ? selectedClass : unselectedClass
            }`}
          >
            <div className="flex items-center">
              <SwatchIcon className="h-5 w-5 mr-2" />
              Patterns
            </div>
          </Link>

          <Link
            to="/docs"
            onClick={() => setIsOpen(false)}
            className={`block pl-6 pr-4 py-2 text-sm ${
              location.pathname.startsWith('/docs') ? selectedClass : unselectedClass
            }`}
          >
            <div className="flex items-center">
              <BookOpenIcon className="h-5 w-5 mr-2" />
              Docs
            </div>
          </Link>

          {isDevelopment && (
            <Link
              to="/debug/config"
              onClick={() => setIsOpen(false)}
              className={`block pl-6 pr-4 py-2 text-sm ${
                location.pathname === '/debug/config' ? selectedClass : unselectedClass
              }`}
            >
              <div className="flex items-center">
                <WrenchScrewdriverIcon className="h-5 w-5 mr-2" />
                Debug Config
              </div>
            </Link>
          )}

          {/* Account section */}
          <div className="border-t border-default my-1" />
          <p className="text-xs font-semibold uppercase text-muted px-4 py-1">Account</p>

          <div>
            {user && (
              <div className="px-4 py-2 border-b border-default">
                <p className="text-sm font-medium text-foreground truncate">
                  {user.displayName || 'User'}
                </p>
                <p className="text-xs text-muted truncate">{user.email}</p>
              </div>
            )}

            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              className={`block px-4 py-2 text-sm ${
                location.pathname === '/profile' ? selectedClass : unselectedClass
              }`}
            >
              <div className="flex items-center">
                <UserCircleIcon className="h-5 w-5 mr-2" />
                Profile
              </div>
            </Link>

            <Link
              to="/settings/invitations"
              onClick={() => setIsOpen(false)}
              className={`block px-4 py-2 text-sm ${
                location.pathname === '/settings/invitations' ? selectedClass : unselectedClass
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <EnvelopeIcon className="h-5 w-5 mr-2" />
                  Invitations
                </div>
                {invitationCount > 0 && (
                  <span className="bg-primary text-primary-foreground text-xs font-semibold px-2 py-0.5 rounded-full">
                    {invitationCount}
                  </span>
                )}
              </div>
            </Link>

            {/* Org/Team Links - only show when user has org */}
            {currentOrg && !tenantLoading && (
              <>
                <Link
                  to="/org/settings"
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-2 text-sm ${
                    location.pathname === '/org/settings' ? selectedClass : unselectedClass
                  }`}
                >
                  <div className="flex items-center">
                    <BuildingOffice2Icon className="h-5 w-5 mr-2" />
                    Organization Settings
                  </div>
                </Link>
              </>
            )}

            <button
              type="button"
              onClick={() => {
                toggleDarkMode();
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-sm text-left text-muted hover:bg-surface-elevated dark:hover:bg-surface-elevated flex items-center"
            >
              {isDarkMode ? (
                <>
                  <SunIcon className="h-5 w-5 mr-2" />
                  Light Mode
                </>
              ) : (
                <>
                  <MoonIcon className="h-5 w-5 mr-2" />
                  Dark Mode
                </>
              )}
            </button>

            {/* Logout Button */}
            <button
              type="button"
              onClick={onLogout}
              className="w-full px-4 py-2 text-sm text-left text-danger-600 dark:text-danger-400 hover:bg-surface-elevated dark:hover:bg-surface-elevated flex items-center"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
